// Asset Details page showing assets within a lane assignment
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { StatusBadge, LoadingSpinner, Button } from '../components/Common';
import { useAuthContext as useAuth } from '../contexts/AuthContext';
import { permissionChecks } from '../lib/auth/helpers';
import { assetService } from '../services/assetService';
import { assetGroupService } from '../services/assetGroupService';
import { useApi } from '../hooks/useApi';
import { 
  useLaneAssignmentData,
  useDashboardLoading,
  useDashboardError,
  useDashboardActions
} from '../store/dashboardMasterStore';
import { useDashboardMasterAuth } from '../hooks/useDashboardMasterAuth';
import { DashboardAssetGroup, DashboardAsset } from '../types/dashboardMaster';
import { getRelativeTime, formatHawaiiDateTime } from '../utils/helpers';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  ArrowPathIcon,
  CubeIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export const AssetDetailsPage: React.FC = () => {
  const { checkpointId, laneAssignmentId } = useParams<{ 
    checkpointId: string; 
    laneAssignmentId: string; 
  }>();
  const navigate = useNavigate();
  const { user } = useAuth(); // Use AuthContext instead of AuthStore
  useDashboardMasterAuth(); // Initialize dashboard service with auth tokens
  const [refreshing, setRefreshing] = useState(false);

  const laneAssignmentIdNum = laneAssignmentId ? parseInt(laneAssignmentId, 10) : 0;
  
  // Get lane assignment data with assets
  const laneData = useLaneAssignmentData(laneAssignmentIdNum);
  const isLoading = useDashboardLoading();
  const error = useDashboardError();
  const { fetchDashboardData, forceRefresh, checkForUpdates } = useDashboardActions();

  useEffect(() => {
    // Fetch data if not available
    if (!laneData && !isLoading) {
      fetchDashboardData();
    }
  }, [laneData, isLoading]); // Remove function dependency to prevent infinite loop

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await forceRefresh();
      toast.success('Asset data refreshed');
    } catch (err) {
      toast.error('Failed to refresh asset data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleBack = () => {
    navigate(`/checkpoint/${checkpointId}/lanes`);
  };

  const handleToggleAssetGroupStatus = async (assetGroupId: number, currentStatus: boolean) => {
    try {
      // This would call the asset group status toggle API
      toast.success(`Asset group ${currentStatus ? 'closed' : 'opened'} successfully`);
      await checkForUpdates();
    } catch (err) {
      toast.error('Failed to toggle asset group status');
    }
  };

  if (isLoading && !laneData) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Error Loading Asset</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <Button onClick={() => navigate('/dashboard')} variant="primary">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!laneData || !laneData.laneAssignment || !laneData.checkpoint || !laneData.terminal) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-white mb-4">Lane Assignment Not Found</h2>
            <p className="text-gray-400 mb-6">
              The requested lane assignment could not be found.
            </p>
            <Button onClick={handleBack} variant="primary">
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Lanes
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { laneAssignment, checkpoint, terminal, computedStats } = laneData;

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Button
              onClick={handleBack}
              variant="secondary"
              size="sm"
              className="mr-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Lanes
            </Button>
            <nav className="text-sm text-gray-400">
              <span>Dashboard</span>
              <span className="mx-2">/</span>
              <span>{checkpoint.name}</span>
              <span className="mx-2">/</span>
              <span className="text-white">{laneAssignment.lanetype.type_name} Assets</span>
            </nav>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">{laneAssignment.lanetype.type_name}</h1>
              <p className="mt-2 text-gray-400">
                {terminal.name} • {checkpoint.name} • {laneAssignment.lanetype.description}
              </p>
              <div className="flex items-center space-x-4 mt-2">
                <StatusBadge 
                  status={laneAssignment.isOpen ? 'open' : 'closed'} 
                  size="lg" 
                />
                <span className="px-2 py-1 bg-gray-700 rounded text-sm text-gray-300">
                  {laneAssignment.lanetype.purpose}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Updated {formatHawaiiDateTime(laneAssignment.last_updated)} by {laneAssignment.updated_by}
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-2">
              <Button
                onClick={handleRefresh}
                variant="secondary"
                size="sm"
                disabled={refreshing || isLoading}
              >
                <ArrowPathIcon className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center">
              <CubeIcon className="h-6 w-6 text-blue-400" />
              <div className="ml-3">
                <p className="text-sm text-gray-400">Asset Groups</p>
                <p className="text-lg font-bold text-white">{computedStats.assetGroupsCount}</p>
                <p className="text-xs text-green-400">{computedStats.openAssetGroups} open</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-6 w-6 text-purple-400" />
              <div className="ml-3">
                <p className="text-sm text-gray-400">Total Assets</p>
                <p className="text-lg font-bold text-white">{computedStats.totalAssets}</p>
                <p className="text-xs text-green-400">{computedStats.openAssets} active</p>
              </div>
            </div>
          </div>
        </div>

        {/* Asset Groups */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-white">
            Asset Groups ({laneAssignment.assetgroups.length})
          </h2>

          {laneAssignment.assetgroups.length === 0 ? (
            <div className="text-center py-12 bg-gray-800 rounded-lg">
              <CubeIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Asset Groups</h3>
              <p className="text-gray-400">
                No asset groups have been configured for this lane assignment.
              </p>
            </div>
          ) : (
            laneAssignment.assetgroups.map((assetGroup: DashboardAssetGroup) => (
              <div key={assetGroup.assetgroup_id} className="bg-gray-800 rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <h3 className="text-lg font-medium text-white">
                        Asset Group #{assetGroup.assetgroup_id}
                      </h3>
                      <StatusBadge 
                        status={assetGroup.isOpen ? 'open' : 'closed'} 
                        size="sm" 
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-400">
                        {assetGroup.assets.length} assets
                      </span>
                      <Button
                        onClick={() => handleToggleAssetGroupStatus(assetGroup.assetgroup_id, assetGroup.isOpen)}
                        variant={assetGroup.isOpen ? "danger" : "success"}
                        size="sm"
                      >
                        {assetGroup.isOpen ? (
                          <>
                            <XCircleIcon className="h-4 w-4 mr-1" />
                            Close Group
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            Open Group
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Created {formatHawaiiDateTime(assetGroup.created_date)}
                  </p>
                </div>

                {/* Assets in this group */}
                <div className="divide-y divide-gray-700">
                  {assetGroup.assets.map((asset: DashboardAsset) => (
                    <div key={asset.id} className="px-6 py-4 hover:bg-gray-750">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4">
                            <h4 className="text-white font-medium">{asset.unit_name}</h4>
                            <StatusBadge 
                              status={asset.isOpen ? 'open' : 'closed'} 
                              size="sm" 
                            />
                            <span className="text-sm text-gray-400">
                              {asset.section.name}
                            </span>
                          </div>
                          <p className="text-sm text-gray-400 mt-1">{asset.description}</p>
                          <div className="flex items-center space-x-6 mt-2 text-sm text-gray-500">
                            <span>Section: {asset.section.description || asset.section.name}</span>
                            <span>Created {formatHawaiiDateTime(asset.created_date)}</span>
                            <span className={`font-medium ${asset.isActive ? 'text-green-400' : 'text-red-400'}`}>
                              {asset.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};